import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { posts, localizePost } from "./posts/posts";
import { useLang } from "../i18n/LangContext";
import { FiCalendar } from "react-icons/fi";
import { Tag } from "lucide-react";
import { useHeadingsData } from "./useHeadingsData";
import { useEffect, useRef, useState } from "react";
import Comments from "./Comments";
import { MathJax } from "better-react-mathjax";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t, localePath, formatDate } = useLang();
  const found = posts.find((p) => p.slug === slug);
  const post = found && localizePost(found, lang);
  const contentKey = `${slug}-${lang}`;
  const [readySlug, setReadySlug] = useState(null);
  const ready = readySlug === slug;
  useEffect(() => {
    let timeout;
    const frame = requestAnimationFrame(() => {
      timeout = setTimeout(() => setReadySlug(slug));
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [slug]);

  const anchorRef = useRef(null);
  const prevKeyRef = useRef(contentKey);
  if (prevKeyRef.current !== contentKey) {
    anchorRef.current = prevKeyRef.current.startsWith(`${slug}-`) ? readAnchor() : null;
    prevKeyRef.current = contentKey;
  }
  const restoreAnchor = () => {
    const anchor = anchorRef.current;
    anchorRef.current = null;
    const el = anchor && contentBlocks()[anchor.index];
    if (el) {
      window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - anchor.top, behavior: "instant" });
    }
  };

  const headings = useHeadingsData(ready ? contentKey : null);

  useEffect(() => {
    if (!post) {
      navigate(localePath("/blog"), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, navigate]);

  if (!post) return null;

  return (
    <div className="relative">
      {/* === ÍNDICE FIXED A LA IZQUIERDA DEL CONTENIDO === */}
      {headings.length > 0 && (
        <nav className="hidden min-[1600px]:block fixed top-36 left-[max(0.5rem,calc(50%-50rem))] w-80">
          <div className="rounded-xl p-5 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm">
            <h4 className="font-semibold mb-3 text-lg text-slate-800 dark:text-slate-200">
              {t("blog.toc")}
            </h4>
            <ul className="flex flex-col gap-2 text-base">
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className="text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}

      {/* === CONTENIDO CENTRADO (HEADER + ARTÍCULO) === */}
      <article className="max-w-4xl mx-auto py-6 sm:py-12">
        {/* HEADER */}
        <div className="text-2xl sm:text-3xl font-bold mb-5 text-slate-900 dark:text-slate-100">
          {post.title}
        </div>

        <div className="flex items-center text-slate-500 dark:text-slate-400 mb-2 text-sm sm:text-base">
          {post.category && (
            <div className="inline-flex items-center px-3 py-1 mr-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
              <Tag className="mr-2 w-4 h-4" />
              <span>{post.category}</span>
            </div>
          )}
          <div className="flex items-center text-slate-600 dark:text-slate-400">
            <FiCalendar className="mr-2" />
            <span className="sm:hidden">
              {new Date(post.date).toLocaleDateString("en-GB")}
            </span>
            <span className="hidden sm:inline">
              {formatDate(post.date, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {!post.translated && (
          <p className="text-sm italic text-slate-500 dark:text-slate-400">{t("blog.onlySpanish")}</p>
        )}

        <hr className="my-5 sm:my-7 border-slate-300 dark:border-slate-700" />

        {/* CONTENIDO */}
        {ready ? (
          <motion.div
            key={slug}
            className="prose dark:prose-invert max-w-none text-slate-800 dark:text-slate-300 text-base md:text-lg md:text-justify"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <MathJax key={lang} hideUntilTypeset="first" dynamic={false} onInitTypeset={restoreAnchor}>
              {post.content}
            </MathJax>
          </motion.div>
        ) : (
          <div className="min-h-[60vh]" />
        )}

        {/* COMENTARIOS */}
        {ready && <Comments postSlug={post.slug} />}
      </article>
    </div>
  );
}

const BLOCKS = "p, h2, li, table, img";

function contentBlocks() {
  return document.querySelectorAll(`article .prose :is(${BLOCKS})`);
}

function readAnchor() {
  const blocks = contentBlocks();
  for (let i = 0; i < blocks.length; i++) {
    const { top, bottom } = blocks[i].getBoundingClientRect();
    if (bottom > 0) return { index: i, top };
  }
  return null;
}
